import React, { useState, useEffect, useRef } from 'react';
import { createTransitionResume, updateResumeData, submitResumeFeedback } from '../../../../services/resume.service';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../common/ConfirmModal';
import CreditBadge from '../common/CreditBadge';

const GapAnalysisStep = ({ onNavigate, onDataUpdate, uploadedFile, reviewedData, targetRole, jobDescription, additionalInfo, gapAnalysis, selectedExperiences = [] }) => {
  const [analyzing, setAnalyzing] = useState(!gapAnalysis);
  const [processing, setProcessing] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

  // Feedback State
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [feedbackMissingCategory, setFeedbackMissingCategory] = useState('');
  const [feedbackBlinking, setFeedbackBlinking] = useState(false);
  const feedbackRef = useRef(null);

  const resumeId = uploadedFile?.id || reviewedData?.id;
  const storageKey = `feedback_FEEDBACK_STEP_4_BRIDGE_${resumeId}`;

  useEffect(() => {
    if (resumeId && localStorage.getItem(storageKey)) {
      setFeedbackSubmitted(true);
    }
  }, [resumeId, storageKey]);

  const handleFeedbackSubmit = async () => {
    if (!feedbackCategory) {
      toast.error('Please select an option');
      return false;
    }
    if (feedbackCategory === '❌ No, something\'s missing' && !feedbackMissingCategory.trim()) {
      toast.error('Please specify which category is missing');
      return false;
    }
    try {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      await submitResumeFeedback({
        user_id: userId,
        category: category,
        resume_id: resumeId || null,
        step: 'FEEDBACK_STEP_4_BRIDGE',
        feedback_data: {
          categoryFound: feedbackCategory,
          missingCategory: feedbackMissingCategory
        }
      });
      setFeedbackSubmitted(true);
      if (resumeId) {
        localStorage.setItem(storageKey, 'true');
      }
      toast.success('Thank you for your feedback!');
      return true;
    } catch (error) {
      toast.error('Failed to submit feedback');
      return false;
    }
  };

  useEffect(() => {
    if (gapAnalysis) {
      setAnalyzing(false);
    } else {
      const timer = setTimeout(() => {
        const initialGapAnalysis = {
          isSignificantPivot: true,
          currentRole: reviewedData?.experience?.[0]?.position || 'Current Position',
          targetRole: targetRole,
          matchScore: 45,
          missingSkills: ['Product Management', 'Agile Methodology', 'Stakeholder Management']
        };
        onDataUpdate({ gapAnalysis: initialGapAnalysis });
        setAnalyzing(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [gapAnalysis, reviewedData, targetRole, onDataUpdate]);

  const experienceOptions = [
    { id: 'freelancing', icon: 'handshake', title: 'Freelancing / Consulting', description: 'Independent contract and professional gigs' },
    { id: 'internship', icon: 'school', title: 'Internship', description: 'Paid or unpaid professional training' },
    { id: 'probono', icon: 'volunteer_activism', title: 'Pro-bono / Volunteer', description: 'Non-profit or community impact work' },
    { id: 'certification', icon: 'verified', title: 'Certifications', description: 'Industry-standard professional credentials' },
    { id: 'minortasks', icon: 'assignment', title: 'Relevant org tasks', description: 'Related duties from previous roles' },
    { id: 'portfolio', icon: 'folder_special', title: 'Personal Projects', description: 'Self-initiated high-impact projects' }
  ];

  const toggleExperience = (id) => {
    const newSelected = selectedExperiences.includes(id) 
      ? selectedExperiences.filter(exp => exp !== id) 
      : [...selectedExperiences, id];
    onDataUpdate({ selectedExperiences: newSelected });
  };

  const handleContinue = async () => {
    if (!feedbackSubmitted) {
      if (feedbackCategory && (feedbackCategory !== '❌ No, something\'s missing' || feedbackMissingCategory.trim())) {
        const success = await handleFeedbackSubmit();
        if (!success) return;
      } else {
        toast.error('Please complete the feedback section or click "Submit Feedback".');
        if (feedbackRef.current) {
          feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setFeedbackBlinking(true);
          setTimeout(() => setFeedbackBlinking(false), 2000);
        }
        return;
      }
    }
    if (selectedExperiences.length === 0) return;
    onNavigate('experience-builder', {
      uploadedFile,
      reviewedData,
      targetRole,
      jobDescription,
      additionalInfo,
      selectedExperiences,
      gapAnalysis
    });
  };

  const handleSkip = async () => {
    if (!feedbackSubmitted) {
      if (feedbackCategory && (feedbackCategory !== '❌ No, something\'s missing' || feedbackMissingCategory.trim())) {
        const success = await handleFeedbackSubmit();
        if (!success) return;
      } else {
        toast.error('Please complete the feedback section or click "Submit Feedback".');
        if (feedbackRef.current) {
          feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setFeedbackBlinking(true);
          setTimeout(() => setFeedbackBlinking(false), 2000);
        }
        return;
      }
    }
    const performResumeCreation = async () => {
      setProcessing(true);
      try {
        const response = await createTransitionResume({
          filename: uploadedFile.filename || uploadedFile.file?.filename,
          reviewedData: reviewedData,
          targetRole: targetRole,
          jobDescription: jobDescription,
          additionalInfo: additionalInfo || '',
          user_id: localStorage.getItem('user_id'),
          category: localStorage.getItem('category'),
          stage: uploadedFile?.stage || 1,
          isManual: false
        });

        // ─── FIX Bug 2: Persist the AI-generated resumeData to the DB immediately ───
        // This ensures the editor pre-fills correctly on session restore.
        const generatedData = response.result || response;
        const resumeId = uploadedFile?.id;
        if (resumeId && generatedData.resumeData) {
          try {
            await updateResumeData(resumeId, {
              ...generatedData.resumeData,
              step: 'preview',
              targetRole,
              jobDescription
            });
          } catch (dbErr) {
            console.warn('Failed to persist generated resumeData to DB:', dbErr.message);
          }
        }

        // ─── FIX Bug 1: Always attach uploadedFile.id so PreviewStep can update the record ───
        const enrichedResult = {
          ...generatedData,
          id: generatedData.id || resumeId,
          resume_id: generatedData.resume_id || resumeId,
        };

        onNavigate('preview', { 
          result: enrichedResult, 
          type: 'create', 
          targetRole, 
          reviewedData, 
          uploadedFile, 
          gapAnalysis 
        });
      } catch (error) {
        console.error('Failed to create resume:', error);
        toast.error('Failed to create resume. Please try again.');
      } finally {
        setProcessing(false);
        setConfirmConfig({ isOpen: false });
      }
    };

    const score = Number(gapAnalysis?.atsScore || gapAnalysis?.matchScore || 0);
    if (score < 80) {
      setConfirmConfig({
        isOpen: true,
        title: 'Low ATS Score Detected',
        message: `Your current ATS score is ${score}%. A low score might not yield the best results. Would you like to add more information or proceed anyway?`,
        confirmLabel: 'Proceed Anyway',
        cancelLabel: 'Add More Info',
        variant: 'info',
        onConfirm: performResumeCreation
      });
      return;
    }

    performResumeCreation();
  };

  if (analyzing) {
    return (
      <div className="bg-slate-50 flex flex-col items-center justify-center p-8 text-center min-h-screen">
        <div className="bg-white border border-slate-200 rounded-3xl p-12 max-w-md shadow-md animate-fade-up">
          <div className="w-16 h-16 border-4 border-blue-light border-t-blue rounded-full animate-spin mx-auto mb-6"></div>
          <h3 className="text-[20px] font-bold text-navy font-montserrat mb-2">Analyzing Transition...</h3>
          <p className="text-[14px] text-slate-500 font-medium leading-relaxed">Cross-referencing your profile with <strong>{targetRole}</strong> benchmarks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-[10px] cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <button 
              type="button"
              className="flex items-center gap-2 px-4 h-10 bg-white border border-slate-200 rounded-xl text-[14px] font-bold text-slate-600 hover:text-navy hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm whitespace-nowrap"
            >
              <span className="msi text-[18px]">arrow_back</span>
              Back to Dashboard
            </button>
          </div>
          <div className="flex items-center gap-6">
            <CreditBadge />
            <div className="flex items-center gap-3">
              {[
                { n: 1, l: 'Upload', s: 'green' },
                { n: 2, l: 'Review', s: 'green' },
                { n: 3, l: 'Bridge', s: 'blue' },
                { n: 4, l: 'Finalize', s: 'slate' }
              ].map((step, i) => (
                <React.Fragment key={step.n}>
                  <div className={`flex items-center gap-2 text-[13px] font-semibold ${step.s === 'green' ? 'text-premium-green' : step.s === 'blue' ? 'text-blue' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full ${step.s === 'green' ? 'bg-premium-green text-white' : step.s === 'blue' ? 'border-2 border-blue bg-blue-light text-blue' : 'border-2 border-slate-200 bg-slate-50 text-slate-400'} flex items-center justify-center text-[11px]`}>{step.n}</div>
                    <span className="hidden sm:inline">{step.l}</span>
                  </div>
                  {i < 3 && <div className="w-6 h-[2px] bg-slate-200" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1000px] mx-auto px-8 py-10 w-full flex-1">
        <div className="inline-flex items-center gap-1.5 text-slate-400 text-[13px] font-semibold cursor-pointer transition-all hover:text-blue mb-6 font-inter underline-offset-4 hover:underline"
          onClick={() => onNavigate('targeting-gap', { uploadedFile, reviewedData })}>
          <span className="msi font-bold">arrow_back</span> Back
        </div>

        <div className="mb-10 animate-fade-up">
          <h1 className="text-[32px] font-extrabold text-navy tracking-[-0.8px] mb-2 font-montserrat tracking-tight">Bridge the Gap</h1>
          <p className="text-[15px] text-slate-500 font-inter leading-relaxed max-w-2xl">
            We identified a strategic mismatch. Let's incorporate missing experiences to improve your ATS score for <strong>{targetRole}</strong>.
          </p>
        </div>

        {/* Transition Alert */}
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm transition-all duration-300 hover:shadow-md bg-gradient-to-r from-premium-amber-light/40 to-yellow-50/40 border-amber-200 p-6 mb-8 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-[10px] bg-premium-amber-light text-premium-amber flex items-center justify-center shrink-0">
              <span className="msi">history_edu</span>
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-amber-900 font-montserrat mb-1">Career Pivot Strategy</h3>
              <p className="text-[13.5px] text-amber-800/80 font-inter leading-relaxed">
                You're moving from <strong>{gapAnalysis?.currentRole}</strong> to <strong>{gapAnalysis?.targetRole}</strong>.
                Your current keyword match is <strong>{gapAnalysis?.atsScore || gapAnalysis?.matchScore}%</strong>.
                Selecting relevant sidework below will help us translate your potential into professional results.
              </p>
            </div>
          </div>
        </div>

        {/* Experience Selection */}
        <div className="animate-fade-up" style={{ animationDelay: '0.15s' }}>
          <h2 className="text-[20px] font-bold text-navy mb-2 font-montserrat tracking-tight">Select your relevant side-experiences</h2>
          <p className="text-[14px] text-slate-500 font-inter mb-8">What have you done independently or in tandem with your main roles?</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {experienceOptions.map((opt) => (
              <div
                key={opt.id}
                onClick={() => toggleExperience(opt.id)}
                className={`bg-white border border-slate-200 rounded-3xl shadow-sm p-6 cursor-pointer transition-all hover:shadow-xl group relative overflow-hidden ${selectedExperiences.includes(opt.id) ? 'border-blue bg-blue-light/30 shadow-md ring-1 ring-blue-500/10' : 'hover:border-blue-200'}`}
              >
                {selectedExperiences.includes(opt.id) && (
                  <div className="absolute top-0 right-0 w-12 h-12 bg-blue-500 text-white flex items-center justify-center rounded-bl-3xl translate-x-1 -translate-y-1 shadow-lg">
                    <span className="msi text-[18px]">done_all</span>
                  </div>
                )}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 ${selectedExperiences.includes(opt.id) ? 'bg-blue-500 text-white shadow-xl shadow-blue-500/20' : 'bg-slate-100 text-slate-500'}`}>
                  <span className="msi text-[24px]">{opt.icon}</span>
                </div>
                <h3 className={`text-[15px] font-bold font-montserrat mb-1.5 ${selectedExperiences.includes(opt.id) ? 'text-navy' : 'text-slate-700'}`}>{opt.title}</h3>
                <p className="text-[12px] text-slate-500 font-inter leading-normal">{opt.description}</p>
              </div>
            ))}
          </div>

          {/* <div className="text-center mb-10">
            <button
              onClick={() => onNavigate('career-roadmap', { uploadedFile, reviewedData, targetRole, jobDescription, additionalInfo, gapAnalysis })}
              className="px-8 py-3 bg-white border-2 border-slate-200 text-slate-500 rounded-2xl hover:bg-slate-50 hover:border-blue-500/30 hover:text-blue transition-all flex items-center gap-2 mx-auto justify-center font-bold text-[14px] shadow-sm hover:shadow-md active:scale-95"
              style={{ borderStyle: 'dashed', borderWidth: '2px' }}
            >
              <span className="msi text-[18px]">visibility_off</span> None of the above (Starting from scratch)
            </button>
          </div> */}

          {!feedbackSubmitted ? (
            <div 
              ref={feedbackRef}
              className={`bg-white border border-slate-200 rounded-3xl p-6 shadow-sm mb-10 animate-fade-up transition-all duration-300 ${feedbackBlinking ? 'ring-4 ring-blue-500/50 scale-[1.02]' : ''}`}
            >
              <h3 className="text-[15px] font-bold text-navy mb-4">Did you find the right category for your experience here?</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {['✅ Yes', '❌ No, something\'s missing'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setFeedbackCategory(opt)}
                    className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all border ${feedbackCategory === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              {feedbackCategory === '❌ No, something\'s missing' && (
                <div className="mb-4">
                  <label className="text-[13px] font-semibold text-slate-700 mb-2 block">Which category were you looking for that isn't here?</label>
                  <input
                    type="text"
                    placeholder="Tell us more..."
                    value={feedbackMissingCategory}
                    onChange={(e) => setFeedbackMissingCategory(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}
              
              <button
                type="button"
                onClick={handleFeedbackSubmit}
                disabled={!feedbackCategory || (feedbackCategory === '❌ No, something\'s missing' && !feedbackMissingCategory.trim())}
                className="px-5 py-2 bg-blue-500 text-white rounded-xl text-[13px] font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Feedback
              </button>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-10 flex items-center gap-3">
              <span className="msi text-green-600 text-[20px]">check_circle</span>
              <p className="text-[13.5px] font-semibold text-green-800">Thank you for your feedback! It helps us improve.</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-200 mb-20">
          <button
            className="h-[52px] px-8 bg-white border border-slate-200 text-navy font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:bg-slate-100 hover:border-blue disabled:opacity-60 disabled:cursor-wait text-[14px]"
            onClick={() => onNavigate('targeting-gap', { uploadedFile, reviewedData })}
          >
            Go Back
          </button>
          <div className="flex gap-4">
            <button
              className="h-[52px] px-8 bg-white border border-slate-200 text-navy font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:bg-slate-100 hover:border-blue disabled:opacity-60 disabled:cursor-wait text-[14px] text-slate-500 border-none hover:bg-slate-100"
              onClick={handleSkip}
              disabled={processing}
            >
              {processing ? '...' : 'Skip Optimization'}
            </button>
            <button
              className={`px-10 h-12 text-[14px] group flex items-center gap-2 rounded-xl transition-all ${selectedExperiences.length > 0 ? 'h-[52px] bg-blue-500 text-white font-bold hover:bg-[#0055DD] hover:-translate-y-px shadow-lg shadow-blue-500/20 opacity-100' : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60'}`}
              onClick={handleContinue}
              disabled={processing || selectedExperiences.length === 0}
            >
              <span>Continue</span>
              <span className="msi text-[18px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>
      <ConfirmModal
        {...confirmConfig}
        isLoading={processing}
        onCancel={() => setConfirmConfig({ isOpen: false })}
      />
    </div>
  );
};

export default GapAnalysisStep;
